/* jshint esversion: 6 */
//
// Toggles a password input between masked and plain text via an eye icon.
//
// Markup contract:
//   div data-controller="password-visibility"
//     input data-password-visibility-target="input" type="password"
//     button data-action="click->password-visibility#toggle"
//       svg data-password-visibility-target="icon"
//         use xlink:href=icon_from_svg("icon-eye")
import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['input', 'icon'];

  connect() {
    // The toggle button is a sibling of the simple_form input wrapper, not a
    // sibling of the <input> itself, so its vertical center can't be pinned
    // with plain top/bottom CSS once a hint or error message pushes other
    // content below the input. Anchor it to the input's actual position
    // instead, which stays correct regardless of what else is in the field.
    //
    // The form can still be display:none at connect time (e.g. the sign-up
    // panel, hidden until "Sign Up with Email" is clicked), which would
    // measure a collapsed 0-height box. A ResizeObserver re-measures whenever
    // the input's real box becomes available or changes (e.g. an error
    // message appears above it after a failed submit).
    this._positionToggle();
    this._resizeObserver = new ResizeObserver(() => this._positionToggle());
    this._resizeObserver.observe(this.inputTarget);
  }

  disconnect() {
    this._resizeObserver?.disconnect();
  }

  _positionToggle() {
    const inputRect = this.inputTarget.getBoundingClientRect();
    if (!inputRect.height) return;

    const containerTop = this.element.getBoundingClientRect().top;
    this.element.querySelector('.password-visibility__toggle').style.top = `${inputRect.top - containerTop + inputRect.height / 2}px`;
  }

  toggle() {
    const willShow = this.inputTarget.type === 'password';
    this.inputTarget.type = willShow ? 'text' : 'password';
    // Icon reflects the input's new state: eye = visible, eye-off = hidden.
    this.iconTarget.querySelector('use').setAttribute('xlink:href', this._iconHref(willShow ? 'icon-eye' : 'icon-eye-off'));
    this._announce(willShow);
    // Toggling visibility must not cost the caret: people use this mid-typing.
    this.inputTarget.focus();
  }

  // The eye is a state, not an action, so it is announced as one. Both labels
  // are rendered onto the button by the server, so this swaps translated copy
  // rather than composing English here.
  _announce(shown) {
    const button = this.element.querySelector('.password-visibility__toggle');
    if (!button) return;

    button.setAttribute('aria-pressed', shown ? 'true' : 'false');
    const label = shown ? button.dataset.labelHide : button.dataset.labelShow;
    if (label) button.setAttribute('aria-label', label);
  }

  _iconHref(name) {
    const currentHref = this.iconTarget.querySelector('use').getAttribute('xlink:href');
    return currentHref.replace(/#.*$/, `#${name}`);
  }
}
